import { IArea, IQuestion } from '../api/types/checklist'
import IResult, { ResultAction } from '../api/types/result'
import IResultSet from '../api/types/resultSet'
import RuleEngine from '../offlineMode/RuleEngine'
import { isFilled } from './answerFilled'

// Copies of areas and questions in a process.
// An area is repeated by a "duplicate area" rule (count from the rule or from an answer) and by
// the manual "+" of an area (result_set_area_duplicate_counts); each copy is its own IArea in
// checklist.areas with area_position 1..n and its own question objects, so rules can show or hide
// a question in one copy only. A question is repeated by a "duplicate question" rule (showCount).
// Every answer belongs to one place: question_id + area_position + question_position.

const position = (value?: number | null) => value ?? 1

// the answer of one place
export function findResult(results: IResult[] | undefined, questionId: number, areaPosition = 1, questionPosition = 1): IResult | undefined {
  return (results ?? []).find((r) => r.question_id === questionId
    && position(r.area_position) === areaPosition && position(r.question_position) === questionPosition)
}

export interface AnswerSlot {
  area: IArea
  question: IQuestion
  areaPosition: number
  questionPosition: number
}

// every place a visible question can be answered: visible areas (each copy), visible questions,
// each copy of a question; information texts and the signature pseudo area have no answer
export function answerSlots(areas: IArea[]): AnswerSlot[] {
  const slots: AnswerSlot[] = []
  areas.filter((area) => !area.hide).forEach((area) => {
    area.questions.filter((q) => q && !q.hide && q.question_type !== 'information' && Number(q.id) > 0).forEach((question) => {
      const count = question.showCount ?? 1
      for (let questionPosition = 1; questionPosition <= count; questionPosition++) {
        slots.push({
          area, question, areaPosition: position(area.area_position), questionPosition
        })
      }
    })
  })
  return slots
}

// [filled, total] of one area copy; questions excluded from the progress don't count
export function areaProgress(area: IArea, resultSet: IResultSet): { filled: number, total: number } {
  const slots = answerSlots([{ ...area, hide: false }]).filter((s) => !s.question.exclude_from_progress)
  const filled = slots.filter((s) => {
    const result = findResult(resultSet.results, Number(s.question.id), s.areaPosition, s.questionPosition)
    return !!result && isFilled(result, resultSet)
  }).length
  return { filled, total: slots.length }
}

export interface MissingAnswer extends AnswerSlot {
  attachmentMissing: boolean
}

// required questions (answer or photo) of visible places without them
export function missingRequired(areas: IArea[], resultSet: IResultSet): MissingAnswer[] {
  return answerSlots(areas)
    .filter((s) => s.question.is_required || s.question.has_attachments_required)
    .map((s) => {
      const result = findResult(resultSet.results, Number(s.question.id), s.areaPosition, s.questionPosition)
      const answerMissing = !!s.question.is_required && (!result || !isFilled(result, resultSet))
      const attachmentMissing = !!s.question.has_attachments_required && !(result?.attachments?.length)
      return answerMissing || attachmentMissing ? { ...s, attachmentMissing } : null
    })
    .filter(Boolean)
}

// does a rule of a question in this area target this question? Then the target follows the answer of
// the same copy; targets in other areas follow the answer for all their copies.
export const sameArea = (origin: IQuestion | undefined | null, target: IQuestion) => !!origin
  && String(origin.area_id) === String(target.area_id)

// the actions of the questions of one area copy for targets in the same area: the saved actions of
// the copy's answers, or what the rules give for an empty answer (new copy without answers)
function actionsInsideCopy(questions: IQuestion[], areaPosition: number, results: IResult[]): ResultAction[] {
  const ids = new Set(questions.map((q) => Number(q.id)))
  const actions: ResultAction[] = []
  questions.filter((q) => q.rule_sets?.length).forEach((question) => {
    const result = findResult(results, Number(question.id), areaPosition, 1)
    const own = result?.action
      ?? RuleEngine.checkRules({ question, value: result?.value ?? '' } as IResult, results).action
      ?? []
    own.forEach((a) => {
      const targets = (a.target_question_ids ?? []).filter((id) => ids.has(Number(id)))
      if (targets.length) actions.push({ ...a, target_question_ids: targets })
    })
  })
  return actions
}

// the questions of a new copy of an area: what rules from other areas did to the first copy, and for
// rules inside the area the answers of this copy (or empty answers)
export function questionsOfCopy(base: IQuestion[], areaPosition: number, results: IResult[]): IQuestion[] {
  const questions = base.map((q) => ({ ...q, area_position: areaPosition }))
  const byId = new Map(questions.map((q) => [Number(q.id), q]))
  const actions = actionsInsideCopy(questions, areaPosition, results)
  // like the backend: a question shown by one rule is not hidden by another (hide first, then show)
  const order = ['duplicate_question', 'hide_question', 'show_question']
  order.forEach((type) => actions.filter((a) => a.action === type).forEach((a) => {
    a.target_question_ids.forEach((id) => {
      const target = byId.get(Number(id))
      if (!target) return
      if (type === 'duplicate_question') target.showCount = Math.max(1, a.target_question_count ?? 1)
      else {
        target.hide = type === 'hide_question'
        target.shown_because_of_rule = type === 'show_question'
      }
    })
  }))
  return questions
}

// the copies of one area for a number of copies: existing copies stay as they are (their questions
// keep what their rules did), missing ones are added after the last copy, extra ones removed (their
// answers stay saved and come back when the number grows again); 0 copies hides the area
export function withAreaCopies(areas: IArea[], areaId: number | string, count: number, results: IResult[]): IArea[] {
  const copies = areas.filter((a) => a.id === areaId)
  if (!copies.length) return areas
  const first = copies.find((a) => position(a.area_position) === 1) ?? copies[0]
  const wanted = Math.max(0, count)
  const kept = copies
    .filter((a) => position(a.area_position) <= Math.max(1, wanted))
    // hidden by another rule stays hidden; hidden only for the missing number is shown again
    .map((a) => ({
      ...a, showCount: Math.max(1, wanted), hide: wanted === 0 || (!!a.hide && !a.hiddenByCount), hiddenByCount: wanted === 0
    }))
  for (let areaPosition = kept.length + 1; areaPosition <= wanted; areaPosition++) {
    kept.push({
      ...first,
      showCount: wanted,
      hide: !!first.hide && !first.hiddenByCount,
      hiddenByCount: false,
      area_position: areaPosition,
      original_area_id: first.id as number,
      is_duplicate: true,
      questions: questionsOfCopy(first.questions, areaPosition, results)
    })
  }
  kept.sort((a, b) => position(a.area_position) - position(b.area_position))
  const index = areas.findIndex((a) => a.id === areaId)
  const others = areas.filter((a) => a.id !== areaId)
  const result = [...others.slice(0, index), ...kept, ...others.slice(index)]
  return result.map((a, i) => ({ ...a, position: i }))
}

// the copies of the areas of a report (frontend_report): the backend sends one entry per copy with
// area_position and the copy's own questions; older responses one entry per area with show_count
// (and copies made by hand only in result_set_area_duplicate_counts)
export function reportAreaCopies(areas: IArea[], resultSet?: IResultSet): IArea[] {
  const withQuestionCopies = (area: IArea, areaPosition: number): IArea => ({
    ...area,
    area_position: areaPosition,
    questions: area.questions.map((q) => ({ ...q, showCount: q.show_count ?? q.showCount ?? 1 }))
  })
  if (areas.some((a) => a.area_position)) {
    return areas.filter((a) => !a.hide).map((a) => withQuestionCopies(a, a.area_position ?? 1))
  }
  return areas.filter((a) => !a.hide).flatMap((area) => {
    const manual = resultSet?.result_set_area_duplicate_counts?.find((c) => c.area_id === area.id)?.area_duplicate_count ?? 0
    const count = Math.max(1, area.show_count ?? 1) + manual
    return Array.from({ length: count }, (_, i) => withQuestionCopies(area, i + 1))
  })
}
